import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import {
  Card,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Switch,
  Button,
  Typography,
} from '@material-ui/core'
import { skuLocaleStateListPromise, skuLocaleStateBulkUpdatePromise } from 'api/SkuLocaleState'

const COLUMNS = [
  { key: 'wlcAvailable', label: 'WLC' },
  { key: 'mskAvailable', label: 'MSK' },
  { key: 'ptAvailable', label: 'PT' },
]

const isDirty = (row, original) => COLUMNS.some(c => !!row[c.key] !== !!original[c.key])

export default function SkuLocaleStateGrid () {
  const [rows, setRows] = useState([])
  const [original, setOriginal] = useState([])
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    const data = await skuLocaleStateListPromise() || []
    setRows(data)
    setOriginal(data)
  }, [])

  useEffect(() => { load() }, [load])

  // Only changed states are sent, so the admin log records what actually moved rather than the
  // whole table on every save.
  const changed = useMemo(() => rows.filter((row, i) => original[i] && isDirty(row, original[i])), [rows, original])

  const handleToggle = useCallback((stateId, key, value) => {
    setRows(current => current.map(row => row.stateId === stateId ? { ...row, [key]: value } : row))
  }, [])

  const handleToggleColumn = useCallback((key, value) => {
    setRows(current => current.map(row => ({ ...row, [key]: value })))
  }, [])

  function handleSave () {
    setSaving(true)
    skuLocaleStateBulkUpdatePromise(changed.map(row => ({
      stateId: row.stateId,
      wlcAvailable: !!row.wlcAvailable,
      mskAvailable: !!row.mskAvailable,
      ptAvailable: !!row.ptAvailable,
      // Optimistic-concurrency token the API requires back, verbatim from the list. null means this
      // state had no sku_locale_state row when the grid loaded.
      updatedAt: row.updatedAt || null,
    })))
      .then(load)
      // 409 = someone else saved while this grid was open. Reloading is the only useful response:
      // without it the stale tokens stay on screen and every retry fails the same way.
      .catch(err => { if (err && err.status === 409) return load() })
      .finally(() => setSaving(false))
  }

  return (
    <div className={'root'}>
      <div className={'content'}>
        <Card>
          <CardContent className={'table-content'}>
            <PerfectScrollbar>
              <div className={'inner'}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>State</TableCell>
                      <TableCell>Code</TableCell>
                      {COLUMNS.map(column => (
                        <TableCell key={column.key}>
                          {column.label}
                          <Checkbox
                            title={`Toggle ${column.label} for every state`}
                            checked={rows.length > 0 && rows.every(row => !!row[column.key])}
                            indeterminate={rows.some(row => !!row[column.key]) && !rows.every(row => !!row[column.key])}
                            onChange={event => handleToggleColumn(column.key, event.target.checked)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow key={row.stateId} hover selected={!!original[i] && isDirty(row, original[i])}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell>{row.code}</TableCell>
                        {COLUMNS.map(column => (
                          <TableCell key={column.key}>
                            <Switch
                              checked={!!row[column.key]}
                              onChange={event => handleToggle(row.stateId, column.key, event.target.checked)}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </PerfectScrollbar>
          </CardContent>

          <CardActions className='flex-right'>
            <Typography variant='body2'>
              {changed.length ? `${changed.length} state(s) changed` : 'No changes'}
            </Typography>
            &nbsp;&nbsp;
            <Button
              color='primary'
              variant='contained'
              disabled={saving || changed.length === 0}
              onClick={handleSave}>
              Save
            </Button>
          </CardActions>
        </Card>
      </div>
    </div>
  )
}
